import { AccountInfo } from '@azure/msal-browser';
import { useCallback, useEffect, useState } from 'react';
import modernAuthenticationService from '../services/ModernAuthenticationService';

/**
 * Hook that returns the current authentication state
 */
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [account, setAccount] = useState<AccountInfo | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
    let isMounted = true;

    const checkAuthState = async () => {
      try {
        // Wait for MSAL to initialize
        await modernAuthenticationService.waitForInitialization();
        
        if (isMounted) {
          const authResult = await modernAuthenticationService.handleRedirectPromise();
          setIsAuthenticated(authResult);
          setAccount(modernAuthenticationService.accountInfo);
        }
      } catch (error) {
        if (isMounted) {
          console.error('Auth state check failed:', error);
          setIsAuthenticated(false);
          setAccount(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    checkAuthState();

    return () => {
      isMounted = false;
    };
  }, []);

  const login = useCallback(async () => {
    try {
      setIsLoading(true);
      await modernAuthenticationService.login();
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      setIsLoading(true);
      await modernAuthenticationService.logout();
      setIsAuthenticated(false);
      setAccount(null);
    } catch (error) {
      console.error('Logout failed:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getToken = useCallback(async (): Promise<string | null> => {
    try {
      return await modernAuthenticationService.getToken();
    } catch (error) {
      console.error('Get token failed:', error);
      return null;
    }
  }, []);

  return {
    isAuthenticated,
    account,
    isLoading,
    login,
    logout,
    getToken
  };
};
export const useAuthState = () => {
  const { isAuthenticated, account } = useAuth();
  return { isAuthenticated, account };
};

/**
 * Hook that returns only the authentication status
 */
export const useIsAuthenticated = (): boolean => {
  const { isAuthenticated } = useAuthState();
  return isAuthenticated;
};

/**
 * Hook that returns the current account information
 */
export const useAccount = (): AccountInfo | null => {
  const { account } = useAuthState();
  return account;
};

/**
 * Hook for getting access tokens
 */
export const useAccessToken = () => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const acquireToken = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Ensure MSAL is initialized before token acquisition
      await modernAuthenticationService.waitForInitialization();
      
      const accessToken = await modernAuthenticationService.getToken();
      setToken(accessToken);
      return accessToken;
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Token acquisition failed');
      setError(error);
      setToken(null);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    token,
    isLoading,
    error,
    acquireToken
  };
};
